/* Member routes. Every one carries the Bearer token; a 401 tears the session
 * down through the transport.
 *
 * SERVER IS TRUTH. Saved properties, listings, threads and sessions are read
 * from the server on every visit. Nothing is mirrored into local storage --
 * legacy kept full listing snapshots in `ejarmasr_member_saved` alongside the
 * server's own `saved_properties` with nothing reconciling them
 * (inventory 6.10). There is one copy here, and it is the server's.
 */

import { authDelete, authGet, authPatch, authPost, API_ROOT } from "./client";
import { getToken } from "@/auth/session";
import type { ListingRow } from "./listings";
import { toListing, type Listing } from "./listings";
import type { Profile } from "@/auth/AuthProvider";

/** `limit: null` means unlimited, per the contract. */
export type Quota = {
  used: number;
  limit: number | null;
  allowed: boolean;
  effective_plan: string | null;
};

export type Broadcast = {
  id: string;
  subject: string | null;
  message: string | null;
  date: string | null;
  target_user_id: string | null;
};

export type ThreadMessage = {
  from?: string; sender?: string; text?: string; body?: string; at?: string; date?: string;
};

export type Thread = {
  id: string;
  thread_id: string | null;
  messages: ThreadMessage[] | null;
  participant_a: string | null;
  participant_b: string | null;
  /* v3.9.0 §7 — the other participant, resolved server-side. Absent today:
   * /users is admin-only, so a member cannot look the id up. Typed optional
   * so the UI can use it the moment the worker starts sending it, with no
   * client release. Name and role only; contact details stay behind the
   * plans.contact_enabled gate. */
  counterpart?: { id: string; name: string | null; role: string | null } | null;
  /* v3.9.0 §8 — which property the conversation is about. The column does
   * not exist yet; the header renders the line only when it does. */
  listing_id?: string | null;
  listing?: { id: string; title: string | null; photo_url: string | null } | null;
};

export type AiSession = {
  id: string;
  session_id: string | null;
  transcript: string | null;
  date: string | null;
  listing_id: string | null;
};

export type SavedRow = {
  id: string;
  listing_id: string | null;
  snapshot: Record<string, unknown> | null;
};

const one = <T>(rows: T[]): T | null => rows[0] ?? null;

export const getProfile = (s?: AbortSignal) => authGet<Profile>("my/profile", s).then(one);

export const patchProfile = (fields: Record<string, unknown>) =>
  authPatch<Profile>("my/profile", fields).then(one);

export const getMyListings = (s?: AbortSignal) =>
  authGet<ListingRow>("my/listings", s).then((rows) => rows.map(toListing));

export const getSaved = (s?: AbortSignal) => authGet<SavedRow>("my/saved", s);
export const getThreads = (s?: AbortSignal) => authGet<Thread>("my/threads", s);
export const getBroadcasts = (s?: AbortSignal) => authGet<Broadcast>("my/broadcasts", s);
export const getAiSessions = (s?: AbortSignal) => authGet<AiSession>("my/sessions", s);

export const getListingQuota = (s?: AbortSignal) =>
  authGet<Quota>("my/listing-quota", s).then(one);
export const getAiQuota = (s?: AbortSignal) => authGet<Quota>("my/ai-quota", s).then(one);

export const unsave = (listingId: string) =>
  authDelete(`my/saved?listing_id=eq.${encodeURIComponent(listingId)}`);

export const save = (listingId: string) =>
  authPost("saved_properties", { listing_id: listingId });

/** Start a conversation with a listing's owner. */
/* Names for the people the caller already shares a thread with (v3.8.4).
 * Names only, and only for existing counterparts — it is not a directory, and
 * the client must not treat it as one. An empty map is normal, not an error:
 * a member with no threads has no peers. */
export const getThreadPeers = (s?: AbortSignal) =>
  authGet<Record<string, string>>("my/thread-peers", s).then((rows) => rows[0] ?? {});

/* An inquiry about a listing. `listing_id` is what makes it an inquiry rather
 * than a plain conversation, and it is why a member can ask the same agent
 * about two properties without the second landing in the first thread. */
export const createInquiry = (
  ownerId: string, listingId: string, text: string, from: string,
) =>
  authPost<Thread>("message_threads", {
    participant_b: ownerId,
    listing_id: listingId,
    thread_id: `T-${Date.now()}`,
    /* `from` is the sender's id, not a blank. An unsigned opening message
     * renders on the wrong side of its own conversation, and reads to the
     * recipient as though the platform sent it. */
    messages: [{ from, text, at: new Date().toISOString() }],
  }).then(one);

export const createThread = (otherUserId: string, text: string) =>
  authPost<Thread>("message_threads", {
    participant_b: otherUserId,
    messages: [{ text, at: new Date().toISOString() }],
  });

/* ── Listing writes ───────────────────────────────────────────────────────
 * owner_id and status are forced server-side, per the contract comment, so
 * neither is sent. A quota refusal comes back as LISTING_LIMIT. */

export type ListingDraft = {
  title: string;
  description: string;
  type: string;
  listing_type: string;
  city_id: number | null;
  district_id: number | null;
  country_id: number | null;
  price: number | null;
  currency: string | null;
  beds: number | null;
  baths: number | null;
  kitchens: number | null;
  area: number | null;
  furnished: boolean;
  lat: number | null;
  lng: number | null;
  location_precise: boolean;
  /* The cover, kept as its own column so cards, search results and saved
   * snapshots need no change. It always mirrors photo_urls[0]. */
  photo_url: string | null;
  /* The ordered gallery (migration 002 / proxy v3.8.3). Optional so a client
   * talking to an older proxy still posts successfully. */
  photo_urls?: string[] | null;
  video_url?: string | null;
  has_video?: boolean;
  /* Migration 004. Required by markets whose row sets requires_ad_licence;
   * absent everywhere else, and never sent empty. */
  licence_number?: string | null;
};

export type CreateListingResult =
  | { ok: true; listing: Listing | null }
  | { ok: false; reason: "quota" | "error"; detail?: string }
  /* L1a: the soft ceiling. `ceiling` is the VALUE THE SERVER SENT in the 422 body;
   * the client has no number of its own to show. */
  | { ok: false; reason: "ceiling"; ceiling: number };

export async function createListing(draft: ListingDraft): Promise<CreateListingResult> {
  try {
    const rows = await authPost<ListingRow>("listings", draft as unknown as Record<string, unknown>);
    return { ok: true, listing: rows[0] ? toListing(rows[0]) : null };
  } catch (err) {
    const msg = String((err as Error)?.message ?? "");
    // The server signals a quota refusal; the client never computes the limit.
    if (/LISTING_LIMIT/i.test(msg)) return { ok: false, reason: "quota" };
    if (/LISTING_CEILING_REACHED/.test(msg)) {
      const n = /"ceiling"\s*:\s*(\d+)/.exec(msg);
      if (n) return { ok: false, reason: "ceiling", ceiling: Number(n[1]) };
    }
    return { ok: false, reason: "error", detail: msg.slice(0, 200) };
  }
}

/* ── Photo upload ─────────────────────────────────────────────────────────
 * POST /upload, jpeg/png/webp. THE SIZE CAP IS A ROW (media.max_photo_mb,
 * migration 018): the caller reads it through src/lib/listingLimits.ts and
 * passes it in, and the worker (v3.8.16) enforces the same row for the
 * market named by ?country_id=. There is no number here any more
 * (WIZARD-2 step 1; before that a 5 MB constant lived on this line). */

export const ALLOWED_UPLOAD_TYPES = ["image/jpeg", "image/png", "image/webp"];

export type UploadResult =
  | { ok: true; url: string }
  | { ok: false; reason: "too-large" | "type" | "error"; detail?: string };

export async function uploadPhoto(file: File, opts: { maxBytes: number; countryId?: number | null }): Promise<UploadResult> {
  if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) return { ok: false, reason: "type" };
  if (file.size > opts.maxBytes) return { ok: false, reason: "too-large" };
  try {
    // Verified against the live worker: /upload takes the RAW image bytes with
    // the image's own Content-Type. Multipart is refused outright and a JSON
    // base64 envelope returns 415 with
    // "Content-Type must be image/jpeg, image/png, or image/webp".
    const qs = opts.countryId ? `?country_id=${encodeURIComponent(String(opts.countryId))}` : "";
    const res = await fetch(`${API_ROOT}/upload${qs}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getToken() ?? ""}`,
        // No custom headers: the proxy's CORS allowlist is exactly
        // Content-Type, Authorization and Prefer, so anything else fails
        // preflight. The worker names the stored object itself.
        "Content-Type": file.type,
      },
      body: file,
    });
    const text = await res.text().catch(() => "");
    if (res.status === 413) return { ok: false, reason: "too-large", detail: text.slice(0, 120) }; // v3.8.16: UPLOAD_TOO_LARGE
    if (!res.ok) return { ok: false, reason: "error", detail: `HTTP ${res.status} ${text.slice(0, 120)}` };
    let url: string | null = null;
    try {
      const j = JSON.parse(text) as { photo_url?: string; url?: string };
      url = j.photo_url ?? j.url ?? null;
    } catch { url = text.trim() || null; }
    if (!url) return { ok: false, reason: "error", detail: "no photo_url in response" };
    return { ok: true, url };
  } catch (e) {
    return { ok: false, reason: "error", detail: String((e as Error)?.message ?? "").slice(0, 120) };
  }
}

/* ── WIZARD-2: drafts live on the server (worker v3.8.16) ─────────────────
 * A Draft is a listings row with status Draft. Creating one runs none of the
 * publish rules (photos, ceiling, duplicate guard) -- those bind at submit,
 * `submitListing()` in src/api/lifecycle.ts. Autosave is a PATCH on the
 * owner's own Draft; the worker re-stamps draft_updated_at and refuses any
 * status in the body. Fields are partial: a draft is, by definition, unfinished. */

export type DraftFields = Partial<ListingDraft> & {
  draft_step?: number | null;
  terms_accepted_at?: string | null;
  finishing?: string | null;
  negotiable?: boolean | null;
  installments?: boolean | null;
  deposit_months?: number | null;
  location_source?: string | null;
};

export type DraftRow = ListingRow & {
  status: string;
  draft_step?: number | null;
  draft_updated_at?: string | null;
  terms_accepted_at?: string | null;
};

export type DraftResult =
  | { ok: true; draft: DraftRow }
  | { ok: false; reason: "unauthorized" | "error"; detail?: string };

const draftFailure = (err: unknown): DraftResult => {
  const msg = String((err as Error)?.message ?? "");
  if (/\b401\b|unauthor/i.test(msg)) return { ok: false, reason: "unauthorized" };
  return { ok: false, reason: "error", detail: msg.slice(0, 200) };
};

/** A new Draft for the signed-in member. */
export async function createDraft(fields: DraftFields): Promise<DraftResult> {
  try {
    const rows = await authPost<DraftRow>("listings", { ...fields, draft: true } as Record<string, unknown>);
    return rows[0] ? { ok: true, draft: rows[0] } : { ok: false, reason: "error", detail: "no row returned" };
  } catch (err) { return draftFailure(err); }
}

/** Autosave: a partial write to my own Draft. Never changes its status. */
export async function saveDraft(id: string, fields: DraftFields): Promise<DraftResult> {
  try {
    const rows = await authPatch<DraftRow>(`listings?id=eq.${encodeURIComponent(id)}`, fields as Record<string, unknown>);
    return rows[0] ? { ok: true, draft: rows[0] } : { ok: false, reason: "error", detail: "no row returned" };
  } catch (err) { return draftFailure(err); }
}

export type DraftLookup =
  | { ok: true; draft: DraftRow | null }
  | { ok: false; reason: "unauthorized" | "error" };

const lookup = async (q: string): Promise<DraftLookup> => {
  try {
    const rows = await authGet<DraftRow>(`my/listings?${q}`);
    return { ok: true, draft: rows[0] ?? null };
  } catch (err) {
    const msg = String((err as Error)?.message ?? "");
    return { ok: false, reason: /\b401\b|unauthor|session/i.test(msg) ? "unauthorized" : "error" };
  }
};

/** One of MY listings by id (the server scopes /my/listings to the token). */
export const getMyListing = (id: string) => lookup(`id=eq.${encodeURIComponent(id)}&limit=1`);

/** My most recently edited Draft, if any -- «استعدنا مسودتك من …». */
export const getLatestDraft = () => lookup("status=eq.Draft&order=draft_updated_at.desc.nullslast&limit=1");

/* WIZARD-2 step 4: one photo with progress. Same POST /upload, same raw-body
 * contract as uploadPhoto(); XHR only because fetch() cannot report upload
 * progress. `maxBytes` is the market's media.max_photo_mb row (the caller
 * reads it); the server enforces the same row and answers 413 above it. */
export function uploadPhotoWithProgress(file: File, opts: {
  maxBytes: number; countryId?: number | null; onProgress?: (fraction: number) => void;
}): { done: Promise<UploadResult>; abort: () => void } {
  if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) return { done: Promise.resolve({ ok: false, reason: "type" }), abort: () => {} };
  if (file.size > opts.maxBytes) return { done: Promise.resolve({ ok: false, reason: "too-large" }), abort: () => {} };
  const xhr = new XMLHttpRequest();
  const done = new Promise<UploadResult>((resolve) => {
    const qs = opts.countryId ? `?country_id=${encodeURIComponent(String(opts.countryId))}` : "";
    xhr.open("POST", `${API_ROOT}/upload${qs}`);
    xhr.setRequestHeader("Authorization", `Bearer ${getToken() ?? ""}`);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total); };
    xhr.onerror = () => resolve({ ok: false, reason: "error", detail: "network" });
    xhr.onabort = () => resolve({ ok: false, reason: "error", detail: "aborted" });
    xhr.onload = () => {
      if (xhr.status === 413) return resolve({ ok: false, reason: "too-large", detail: xhr.responseText.slice(0, 120) });
      if (xhr.status < 200 || xhr.status >= 300) return resolve({ ok: false, reason: "error", detail: `HTTP ${xhr.status}` });
      try {
        const j = JSON.parse(xhr.responseText) as { photo_url?: string; url?: string };
        const url = j.photo_url ?? j.url ?? null;
        resolve(url ? { ok: true, url } : { ok: false, reason: "error", detail: "no photo_url in response" });
      } catch { resolve({ ok: false, reason: "error", detail: "bad response" }); }
    };
    xhr.send(file);
  });
  return { done, abort: () => xhr.abort() };
}

/** Update one of my listings. owner_id and status are server-forced. */
export const updateListing = (id: string, fields: Partial<ListingDraft>) =>
  authPatch<ListingRow>(`listings?id=eq.${encodeURIComponent(id)}`, fields as Record<string, unknown>)
    .then((rows) => (rows[0] ? toListing(rows[0]) : null));

/* The signed-in member's own agent-verification request, if any.
 * The proxy scopes this to `or=(user_id.eq.<me>,email.eq.<my email>)` and
 * selects only id, status, reject_reason and business_name -- an applicant
 * cannot read anyone else's request or their own submitted documents. */
export type MyVerification = {
  id: string;
  status: string | null;
  reject_reason: string | null;
  business_name: string | null;
};

export const getMyVerification = (s?: AbortSignal) =>
  authGet<MyVerification>("my/verification", s).then((rows) => rows[0] ?? null);

/* Append to a thread. The proxy accepts only `messages` on this route and
 * checks the caller is one of the two participants, so the whole array is
 * sent back with the new entry on the end -- there is no append primitive. */
export const updateThread = (id: string, messages: ThreadMessage[]) =>
  authPatch<Thread>(`message_threads?id=eq.${encodeURIComponent(id)}`, { messages });

/* One of the member's OWN listings by id, whatever its status.
 *
 * The public GET /listings forces status=eq.Active, so an owner following a
 * link to their own Pending or Rejected listing got the "unavailable" state
 * on a listing they had just written (defect D-W8). /my/listings is scoped to
 * owner_id server-side, so this returns a row only for its actual owner --
 * the fallback cannot leak anyone else's unpublished listing.
 */
export const getMyListingById = (id: string, s?: AbortSignal) =>
  authGet<ListingRow>(`my/listings?id=eq.${encodeURIComponent(id)}&limit=1`, s)
    .then((rows) => (rows[0] ? toListing(rows[0]) : null));

/* The owner's Active <-> Paused PATCH (setListingStatus) left with L1b: every
 * lister status change goes through POST /listings/{id}/{action} in
 * src/api/lifecycle.ts, where the worker's matrix, ceiling and expiry rule. */

/* ── account deletion ─────────────────────────────────────────────────────
 * Bassem's ruling, 2026-09-26, required by Google Play.
 *
 * This asks for it; it does not do it. The worker deactivates the account,
 * unpublishes its listings and closes every other private route, and DELETES
 * NOTHING — the hard delete is a scheduled job 30 days later. Signing in
 * before then cancels the whole thing, which is why nothing may be destroyed
 * at this point.
 *
 * The same route serves the mobile app: it is a POST with a member token and
 * no body.
 */
export type DeleteAccountResult =
  | { ok: true; requestedAt: string; days: number; unpublished: number }
  | { ok: false; detail?: string };

export async function requestAccountDeletion(): Promise<DeleteAccountResult> {
  try {
    const rows = await authPost<{
      ok?: boolean; deletion_requested_at?: string; days?: number; unpublished?: number;
    }>("my/delete-account", {});
    const r = rows[0];
    if (!r?.ok) return { ok: false };
    return {
      ok: true,
      requestedAt: r.deletion_requested_at ?? new Date().toISOString(),
      days: r.days ?? 30,
      unpublished: r.unpublished ?? 0,
    };
  } catch (err) {
    return { ok: false, detail: String((err as Error)?.message ?? "").slice(0, 200) };
  }
}
